const LOOKBOOK_HOSTNAME = "lookbook.otrium.com";

const STATIC_ALIAS = "/static";

function toInternalPath(src: string): string {
  return src.startsWith(`${STATIC_ALIAS}/`)
    ? src.slice(STATIC_ALIAS.length)
    : src;
}

type ImageLoaderParams = {
  src: string;
  width?: number;
  height?: number;
  quality?: number;
};

/**
 * Custom Next.js image loader that uses Cloudflare Image Resizing
 * for lookbook.otrium.com images instead of the default /_next/image proxy.
 *
 * Cloudflare resizes at the edge — no Node.js processing, better caching,
 * and automatic AVIF/WebP format selection.
 *
 * For non-lookbook URLs, falls back to the default Next.js image loader
 * behavior (/_next/image).
 *
 * Usage with Next.js <Image>:
 *   <Image loader={lookbookImageLoader} src={url} ... />
 *
 * For plain <img> tags:
 *   <img src={getLookbookImageUrl(url, { width: 300 })} />
 */
export function lookbookImageLoader({
  src,
  width,
  height,
  quality,
}: ImageLoaderParams): string {
  if (isLookbookUrl(src)) {
    return getLookbookImageUrl(src, { width, height, quality });
  }
  // Fall back to default Next.js image loader for non-lookbook URLs
  const widthParam = width ? `&w=${width}` : "";
  return `/_next/image?url=${encodeURIComponent(
    toInternalPath(src)
  )}${widthParam}&q=${quality || 75}`;
}

const GATED_IMAGE_BLUR = 10;

export function blurredLookbookImageLoader({
  src,
  width,
  height,
  quality,
}: ImageLoaderParams): string {
  if (isLookbookUrl(src)) {
    return getLookbookImageUrl(src, {
      width,
      height,
      quality,
      blur: GATED_IMAGE_BLUR,
    });
  }
  return lookbookImageLoader({ src, width, height, quality });
}

/**
 * Transforms a lookbook.otrium.com URL to use Cloudflare Image Resizing.
 *
 * Before: https://lookbook.otrium.com/wp-content/uploads/2025/06/product.jpg
 * After:  https://lookbook.otrium.com/cdn-cgi/image/width=300,quality=80,format=auto/wp-content/uploads/2025/06/product.jpg
 *
 * Non-lookbook URLs are returned unchanged.
 *
 * See docs/tech/responsive-images.md for when to cap width vs height and when
 * to pass an explicit format.
 */
export function getLookbookImageUrl(
  src: string,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    format?: string;
    blur?: number;
  } = {}
): string {
  const { width, height, quality = 80, format = "auto", blur } = options;

  if (!isLookbookUrl(src)) {
    return src;
  }

  try {
    const url = new URL(src);
    const params = [`quality=${quality}`, `format=${format}`];
    if (width) {
      params.push(`width=${width}`);
    }
    if (height) {
      params.push(`height=${height}`);
    }
    if (blur) {
      params.push(`blur=${blur}`);
    }
    return `${url.origin}/cdn-cgi/image/${params.join(",")}${url.pathname}`;
  } catch {
    return src;
  }
}

function isLookbookUrl(src: string): boolean {
  try {
    return new URL(src).hostname === LOOKBOOK_HOSTNAME;
  } catch {
    return false;
  }
}
